import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, X, Send, Loader2, MessageSquare, AlertCircle, Bot, User,
  ChevronRight, ChevronLeft, ExternalLink,
} from "lucide-react";
import { apiClient } from "../lib/api";
import { useI18n } from "../lib/i18n";

const STORAGE_KEY_SESSION = "cartal:ai:session_id";

export default function AiHelpButton() {
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY_SESSION); } catch { return null; }
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState(
    lang === "ar"
      ? ["كم تكلفة ضمان كارتال؟", "كيف أبيع سيارتي؟", "ما الباقات المتاحة؟"]
      : ["How much does Escrow cost?", "How do I sell my car?", "What plans are available?"]
  );
  const [escalateOpen, setEscalateOpen] = useState(false);
  const scrollerRef = useRef(null);

  // Persist session id whenever it changes.
  useEffect(() => {
    if (sessionId) {
      try { sessionStorage.setItem(STORAGE_KEY_SESSION, sessionId); } catch { /* ignore */ }
    }
  }, [sessionId]);

  // Auto-scroll on new message
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = useCallback(async (text) => {
    const message = (text || input).trim();
    if (!message || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message, ts: Date.now() }]);
    setBusy(true);
    try {
      const { data } = await apiClient.post("/ai/chat", {
        message,
        session_id: sessionId,
        lang,
      });
      if (!sessionId && data.session_id) setSessionId(data.session_id);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: data.reply,
          confidence: data.confidence,
          sources: data.sources || [],
          fallback: data.fallback,
          ts: Date.now(),
        },
      ]);
      if (data.suggestions?.length) setSuggestions(data.suggestions);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: lang === "ar"
            ? "حدث خطأ في الاتصال بالمساعد. حاول لاحقاً أو تواصل مع الإدارة."
            : "Could not reach the assistant. Please try later or contact the administrator.",
          isError: true,
          ts: Date.now(),
        },
      ]);
    } finally {
      setBusy(false);
    }
  }, [input, busy, sessionId, lang]);

  const Arrow = lang === "ar" ? ChevronLeft : ChevronRight;

  return (
    <>
      {/* Floating Button (bottom-end, lifted on mobile to clear the app-install banner) */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed z-40 bottom-24 md:bottom-5 end-5 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-transform hover:scale-110 group"
          style={{
            background: "linear-gradient(135deg, #c9a55c 0%, #8a6f2e 100%)",
            color: "#0a0a0a",
          }}
          aria-label={lang === "ar" ? "افتح المساعد الذكي" : "Open AI Assistant"}
          data-testid="ai-help-fab"
        >
          <Sparkles className="w-6 h-6" />
          <span className="absolute -top-1 -end-1 w-3 h-3 rounded-full bg-green-500 animate-pulse" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed z-50 bottom-24 md:bottom-5 end-5 w-[92vw] max-w-[420px] h-[70vh] sm:h-[640px] rounded-3xl flex flex-col overflow-hidden shadow-2xl"
          style={{
            background: "linear-gradient(160deg, #0a0a0a 0%, #1a1a1a 50%, #2a1f10 100%)",
            border: "1.5px solid #c9a55c70",
          }}
          data-testid="ai-help-panel"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3" style={{ background: "rgba(0,0,0,0.4)" }}>
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: "rgba(201,165,92,0.2)", border: "1px solid rgba(201,165,92,0.6)" }}
            >
              <Sparkles className="w-5 h-5" style={{ color: "#d4b56e" }} />
            </div>
            <div className="flex-1">
              <div className="font-display text-base" style={{ color: "#f7eecd" }}>
                {lang === "ar" ? "كارتال هلپر" : "CARTAL Helper"}
              </div>
              <div className="text-[11px]" style={{ color: "#d4b56e" }}>
                {lang === "ar" ? "ذكاء اصطناعي · يجيب من قاعدة المعرفة الرسمية" : "AI · Answers from official knowledge base"}
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center" data-testid="ai-help-close" aria-label={lang === "ar" ? "إغلاق" : "Close"}>
              <X className="w-5 h-5" style={{ color: "#d4b56e" }} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="ai-messages">
            {messages.length === 0 ? (
              <WelcomeBlock lang={lang} suggestions={suggestions} onPick={send} />
            ) : (
              messages.map((m, i) => <MessageBubble key={i} idx={i} msg={m} lang={lang} />)
            )}
            {busy && (
              <div className="flex items-center gap-2 px-3 py-2 max-w-[80%] rounded-2xl" style={{ background: "rgba(255,255,255,0.06)" }}>
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#d4b56e" }} />
                <span className="text-xs" style={{ color: "#d4b56e" }}>
                  {lang === "ar" ? "كارتال هلپر يكتب..." : "CARTAL Helper is typing..."}
                </span>
              </div>
            )}
          </div>

          {/* Quick suggestions */}
          {!busy && messages.length > 0 && suggestions.length > 0 && (
            <div className="px-4 pb-2 flex gap-2 flex-wrap" data-testid="ai-suggestions">
              {suggestions.slice(0, 3).map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  className="text-[11px] px-2.5 py-1 rounded-full hover:bg-white/10 transition-colors"
                  style={{ background: "rgba(201,165,92,0.12)", color: "#d4b56e", border: "1px solid rgba(201,165,92,0.35)" }}
                  data-testid={`ai-suggestion-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Escalation hint */}
          {messages.some((m) => m.role === "assistant" && (m.fallback || (m.confidence ?? 1) < 0.4)) && (
            <div className="px-4 pb-2">
              <button
                onClick={() => setEscalateOpen(true)}
                className="w-full text-xs px-3 py-2 rounded-xl hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
                style={{ background: "rgba(220,38,38,0.12)", color: "#fca5a5", border: "1px solid rgba(220,38,38,0.35)" }}
                data-testid="ai-escalate-btn"
              >
                <AlertCircle className="w-3.5 h-3.5" />
                {lang === "ar" ? "لم تجد إجابتك؟ تواصل مع الإدارة" : "Didn't find your answer? Contact admin"}
              </button>
            </div>
          )}

          {/* Composer */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-2 px-3 py-3 border-t"
            style={{ background: "rgba(0,0,0,0.4)", borderColor: "rgba(201,165,92,0.25)" }}
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={lang === "ar" ? "اكتب سؤالك..." : "Type your question..."}
              className="flex-1 px-3 py-2 rounded-xl text-sm outline-none"
              style={{ background: "rgba(255,255,255,0.06)", color: "#f7eecd", border: "1px solid rgba(201,165,92,0.3)" }}
              data-testid="ai-input"
            />
            <button
              type="submit"
              disabled={!input.trim() || busy}
              className="w-10 h-10 rounded-xl flex items-center justify-center disabled:opacity-40"
              style={{ background: "linear-gradient(135deg,#d4b56e,#c9a55c)", color: "#0a0a0a" }}
              data-testid="ai-send"
              aria-label={lang === "ar" ? "إرسال" : "Send"}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>

          <div className="text-center text-[10px] py-1.5" style={{ background: "rgba(0,0,0,0.4)", color: "rgba(232,225,207,0.5)" }}>
            <Link to="/faq" className="hover:underline">
              {lang === "ar" ? "تصفّح الأسئلة الشائعة الكاملة" : "Browse the full FAQ"} <Arrow className="w-3 h-3 inline" />
            </Link>
          </div>
        </div>
      )}

      {escalateOpen && (
        <AiEscalationModal lang={lang} sessionId={sessionId} onClose={() => setEscalateOpen(false)} />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
function WelcomeBlock({ lang, suggestions, onPick }) {
  return (
    <div className="text-center py-4" data-testid="ai-welcome">
      <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-3" style={{ background: "rgba(201,165,92,0.15)" }}>
        <Bot className="w-7 h-7" style={{ color: "#d4b56e" }} />
      </div>
      <div className="font-display text-lg" style={{ color: "#f7eecd" }}>
        {lang === "ar" ? "مرحباً 👋 كيف أساعدك؟" : "Hi 👋 how can I help?"}
      </div>
      <div className="text-xs mt-1" style={{ color: "rgba(232,225,207,0.7)" }}>
        {lang === "ar" ? "أجيب من قاعدة معرفة كارتال الرسمية فقط — بلا اختراع." : "I answer only from CARTAL's official knowledge base — no guesses."}
      </div>
      <div className="mt-5 space-y-2">
        {suggestions.slice(0, 4).map((s, i) => (
          <button
            key={i}
            onClick={() => onPick(s)}
            className="w-full text-start px-3 py-2 rounded-xl hover:bg-white/5 transition-colors text-sm"
            style={{ background: "rgba(255,255,255,0.04)", color: "#f7eecd", border: "1px solid rgba(201,165,92,0.25)" }}
            data-testid={`ai-welcome-suggestion-${i}`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ msg, lang, idx }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex gap-2 ${isUser ? "flex-row-reverse" : "flex-row"}`} data-testid={isUser ? `ai-message-user-${idx}` : `ai-message-assistant-${idx}`}>
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
        style={{
          background: isUser ? "rgba(255,255,255,0.08)" : "rgba(201,165,92,0.18)",
          color: "#d4b56e",
        }}
      >
        {isUser ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
      </div>
      <div
        className="max-w-[80%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap"
        style={{
          background: isUser ? "linear-gradient(135deg,#d4b56e,#c9a55c)" : "rgba(255,255,255,0.06)",
          color: isUser ? "#0a0a0a" : "#f7eecd",
        }}
      >
        {msg.content}
        {!isUser && msg.sources && msg.sources.length > 0 && (
          <div className="mt-2 pt-2 border-t border-white/10 flex flex-wrap gap-1.5">
            {msg.sources.slice(0, 3).map((s) => (
              <Link
                key={s.id}
                to={`/faq/${s.category}#${s.id}`}
                className="text-[10px] px-2 py-0.5 rounded-full hover:bg-white/10 inline-flex items-center gap-1"
                style={{ background: "rgba(201,165,92,0.1)", color: "#d4b56e", border: "1px solid rgba(201,165,92,0.25)" }}
              >
                {s.question.slice(0, 35)}{s.question.length > 35 ? "…" : ""}
                <ExternalLink className="w-2.5 h-2.5" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AiEscalationModal({ lang, sessionId, onClose }) {
  const [type, setType] = useState("question");
  const [form, setForm] = useState({ subject: "", message: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiClient.post("/ai/escalate", {
        type,
        subject: form.subject || (lang === "ar" ? "تواصل من المساعد" : "AI escalation"),
        message: form.message,
        session_id: sessionId,
        email: form.email || undefined,
      });
      setDone(true);
    } catch (e) {
      // ignore
    } finally {
      setBusy(false);
    }
  };

  const labels = {
    question: lang === "ar" ? "أرسل سؤالاً" : "Submit a question",
    suggestion: lang === "ar" ? "أرسل اقتراحاً" : "Submit a suggestion",
    complaint: lang === "ar" ? "تقديم شكوى" : "Submit a complaint",
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="ai-escalation-modal">
      <div className="bg-white rounded-3xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl">{lang === "ar" ? "تواصل مع الإدارة" : "Contact the administrator"}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-black/5 flex items-center justify-center" data-testid="ai-escalation-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        {done ? (
          <div className="text-center py-4" data-testid="ai-escalation-success">
            <div className="w-12 h-12 mx-auto rounded-full bg-green-100 text-green-700 flex items-center justify-center text-2xl">✓</div>
            <h4 className="font-display text-lg mt-2">{lang === "ar" ? "تم إرسال رسالتك" : "Sent"}</h4>
            <p className="text-sm text-[#525252] mt-1">{lang === "ar" ? "سنرد عليك قريباً" : "We'll respond soon"}</p>
            <button onClick={onClose} className="btn-primary mt-4">{lang === "ar" ? "إغلاق" : "Close"}</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="flex gap-2">
              {(["question", "suggestion", "complaint"]).map(k => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setType(k)}
                  className={`flex-1 px-2 py-2 rounded-lg text-xs ${type === k ? "bg-[#0a0a0a] text-white" : "bg-black/5 text-[#3a3a3a]"}`}
                  data-testid={`ai-esc-type-${k}`}
                >
                  {labels[k].split(" ").slice(-1)[0]}
                </button>
              ))}
            </div>
            <input
              type="text"
              placeholder={lang === "ar" ? "العنوان" : "Subject"}
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="w-full px-3 py-2 border-2 border-black/10 rounded-lg text-sm focus:border-[#c9a55c] outline-none"
              data-testid="ai-esc-subject"
            />
            <textarea
              placeholder={lang === "ar" ? "رسالتك..." : "Your message..."}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={4}
              required
              className="w-full px-3 py-2 border-2 border-black/10 rounded-lg text-sm focus:border-[#c9a55c] outline-none"
              data-testid="ai-esc-message"
            />
            <input
              type="email"
              placeholder={lang === "ar" ? "البريد (اختياري)" : "Email (optional)"}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3 py-2 border-2 border-black/10 rounded-lg text-sm focus:border-[#c9a55c] outline-none"
              data-testid="ai-esc-email"
            />
            <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="ai-esc-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {lang === "ar" ? "إرسال" : "Send"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
